import { ActionIcon } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import commonClasses from "../styles/common.module.css";

interface SidebarToggleProps {
  opened: boolean;
  onToggle: () => void;
  /** Must match the AppShell `navbar.width` of the hosting layout. */
  navbarWidth?: number;
}

/**
 * Desktop sidebar expand / collapse-to-rail control, rendered as a circular
 * chevron pinned to the navbar's right border. The navbar is always present on
 * desktop now — collapsing swaps it to a 72px icon rail rather than hiding it —
 * so this docks on the border in both states, following the live `navbarWidth`.
 * It lives at the shell level (position: fixed) because the navbar border isn't
 * a positioning context. Hidden below `sm`, where the navbar is a
 * burger-triggered overlay instead.
 */
export function SidebarToggle({
  opened,
  onToggle,
  navbarWidth = 260,
}: SidebarToggleProps) {
  return (
    <ActionIcon
      className={commonClasses.touchTarget}
      // Always docked on the sidebar border → bordered "control" look in both
      // the expanded and mini-rail states.
      variant="default"
      radius="xl"
      // `lg` draws 34px, NOT the 44px this comment used to claim — the WCAG
      // 2.5.5 minimum comes from `touchTarget` above, which grows the hit
      // region without growing the button. On iPad landscape this is the only
      // sidebar control, so the tap area is the part that matters.
      size="lg"
      visibleFrom="sm"
      onClick={onToggle}
      aria-label={opened ? "Collapse sidebar" : "Expand sidebar"}
      style={{
        position: "fixed",
        // Top of the sidebar, just below the header — the conventional
        // collapse-control spot, aligned with the "Navigation" label.
        // Derived from the AppShell's own offset rather than the literal 72
        // that used to sit here: on a touch device touch.css grows the header
        // by env(safe-area-inset-top) (84px on iPad), and a hardcoded 72 put
        // this control INSIDE the header. It never showed before because it
        // was hidden below `md`; lowering that to `sm` makes iPad portrait the
        // first viewport that would have hit it.
        top: "calc(var(--app-shell-header-offset, 3.75rem) + 12px)",
        // Half-overlap the navbar's right border; tracks the rail<->expanded
        // width swap via the same easing as the AppShell reflow.
        // -27 rather than -22: the 44px hit region extends 5px past the drawn
        // button, which put it 1px into the first control of the page content
        // (measured against "Back to Cases"). Still reads as docked on the
        // border, now with clearance instead of an ambiguous sliver.
        left: navbarWidth - 27,
        // Above the navbar border (z 101) so it stays clickable even when the
        // sticky AssumedRoleBanner (z 100) is present in the rare assumed-role
        // case — function over the banner being pixel-perfect.
        zIndex: 101,
        transition:
          "left var(--app-shell-transition-duration, 200ms) var(--app-shell-transition-timing-function, ease)",
        // Same defensive repaint hint as AppLayout's navbar `willChange` —
        // this button's own `left` tracks the identical width swap, so it's
        // exposed to the same "state updated, paint didn't" risk. See the
        // comment on the AppShell `styles.navbar` prop for the full context.
        willChange: "left",
      }}
    >
      {opened ? <IconChevronLeft size={18} /> : <IconChevronRight size={18} />}
    </ActionIcon>
  );
}
